/**
 * Storefront Theme Editor Widget
 * 
 * Point-and-click editor for theme tokens (colors, radius).
 * Edits values_json field and provides publish functionality.
 */

import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/useLanguage';
import type { ExperienceWidgetProps } from '@/lib/views/widgets/registry';
import type { StorefrontThemeDoc, ThemeTokenDefinition } from '../types';
import { THEME_TOKENS, THEME_TOKEN_GROUPS, getTokensByGroup } from './registry';
import { hslComponentsToHex, hexToHslComponents } from './color-utils';
import { usePublishStorefrontTheme } from '../api/storefrontThemeHooks';
import { AlertCircle } from 'lucide-react';
import { useCanPublishQuery } from '@/hooks/useCanPublishQuery';

export function StorefrontThemeEditorWidget({
  formData,
  onFieldChange,
  disabled,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const publishTheme = usePublishStorefrontTheme();
  
  // Cast formData to typed doc
  const doc = formData as StorefrontThemeDoc;
  const isNew = !doc.name || doc.name === 'new';

  const { allowed: canPublish } = useCanPublishQuery(
    "Storefront Theme",
    !isNew ? doc.name : undefined
  );
  
  // Parse values_json - memoize to avoid re-parsing on every render
  const tokenValues = useMemo(() => {
    try {
      return JSON.parse(doc.values_json || '{}') as Record<string, string>;
    } catch {
      return {};
    }
  }, [doc.values_json]);
  
  // Track validation errors per token
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Update a single token value
  const handleTokenChange = useCallback((tokenKey: string, value: string) => {
    // Validate if token has constraints
    const token = THEME_TOKENS.find(t => t.key === tokenKey);
    if (token?.constraints?.pattern) {
      if (!token.constraints.pattern.test(value)) {
        setValidationErrors(prev => ({ ...prev, [tokenKey]: 'Invalid format' }));
        return;
      }
    }
    
    // Clear error if validation passes
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[tokenKey];
      return next;
    });
    
    const newValues = { ...tokenValues, [tokenKey]: value };
    onFieldChange('values_json', JSON.stringify(newValues, null, 2));
  }, [tokenValues, onFieldChange]);
  
  // Initialize defaults if values_json is empty
  const handleInitializeDefaults = useCallback(() => {
    const defaults: Record<string, string> = {};
    for (const token of THEME_TOKENS) {
      defaults[token.key] = token.default;
    }
    onFieldChange('values_json', JSON.stringify(defaults, null, 2));
  }, [onFieldChange]);
  
  // Render color token editor
  const renderColorToken = (token: ThemeTokenDefinition) => {
    const currentValue = tokenValues[token.key] || token.default;
    const hexValue = hslComponentsToHex(currentValue);
    const hasError = !!validationErrors[token.key];
    const displayKey = token.key.split('.').pop() || token.key;
    
    return (
      <div key={token.key} className="space-y-2">
        <Label className="text-sm font-medium capitalize">{displayKey}</Label>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={hexValue}
            onChange={(e) => {
              const hsl = hexToHslComponents(e.target.value);
              handleTokenChange(token.key, hsl);
            }}
            disabled={disabled}
            className="w-12 h-12 rounded-sm cursor-pointer border border-border disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="flex-1">
            <Input
              value={currentValue}
              onChange={(e) => handleTokenChange(token.key, e.target.value)}
              disabled={disabled}
              className={`font-mono text-xs ${hasError ? 'border-destructive' : ''}`}
              placeholder={token.default}
            />
            {hasError && (
              <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {validationErrors[token.key]}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Render length token editor
  const renderLengthToken = (token: ThemeTokenDefinition) => {
    const currentValue = tokenValues[token.key] || token.default;
    const hasError = !!validationErrors[token.key];
    const displayKey = token.key.split('.').pop() || token.key;
    
    return (
      <div key={token.key} className="space-y-2">
        <Label className="text-sm font-medium capitalize">{displayKey}</Label>
        <Input
          value={currentValue}
          onChange={(e) => handleTokenChange(token.key, e.target.value)}
          disabled={disabled}
          className={`font-mono text-xs ${hasError ? 'border-destructive' : ''}`}
          placeholder={token.default}
        />
        {hasError && (
          <p className="text-xs text-destructive mt-1 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {validationErrors[token.key]}
          </p>
        )}
      </div>
    );
  };
  
  const handlePublish = async () => {
    if (!doc.name || isNew) {
      alert('Please save the theme first before publishing.');
      return;
    }
    if (!canPublish) {
      alert("You don't have permission to publish this theme.");
      return;
    }
    
    try {
      const result = await publishTheme.mutateAsync({ docname: doc.name });
      if (result.success) {
        // Success - detail query will be invalidated automatically
        // User can see updated last_published_at on next render
      } else {
        alert(`Publish failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      alert(`Publish failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };
  
  // Check if values_json is empty
  const hasValues = Object.keys(tokenValues).length > 0;
  
  return (
    <div key={language} className="space-y-6">
      {/* Tenant Field - editable only for new docs */}
      <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Tenant</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label>Tenant</Label>
            <Input
              value={doc.tenant || ''}
              onChange={(e) => onFieldChange('tenant', e.target.value)}
              disabled={disabled || !isNew}
              placeholder="Enter tenant identifier"
              className={!isNew ? 'bg-muted' : ''}
            />
            {!isNew && (
              <p className="text-xs text-muted-foreground">
                Tenant cannot be changed after creation (autoname constraint)
              </p>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Initialize defaults if empty */}
      {!hasValues && (
        <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-muted-foreground mb-4">No theme tokens configured yet.</p>
            <Button onClick={handleInitializeDefaults} disabled={disabled}>
              Initialize with Defaults
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Token Groups */}
      {hasValues && THEME_TOKEN_GROUPS.map((group) => (
        <Card key={group} className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg capitalize">{group}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {getTokensByGroup(group).map((token) => 
              token.type === 'color' 
                ? renderColorToken(token)
                : renderLengthToken(token)
            )}
          </CardContent>
        </Card>
      ))}
      
      {/* Publish Section */}
      <Card className="rounded-2xl border-0 bg-white dark:bg-gray-900 shadow-sm">
        <CardContent className="py-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {doc.last_published_at 
              ? `Last published: ${new Date(doc.last_published_at).toLocaleString()}`
              : 'Never published'}
            {doc.last_publish_error && (
              <div className="text-destructive mt-1 text-xs">
                Error: {doc.last_publish_error}
              </div>
            )}
          </div>
          <Button 
            onClick={handlePublish}
            disabled={disabled || publishTheme.isPending || isNew || !hasValues || !canPublish}
          >
            {publishTheme.isPending ? 'Publishing...' : 'Publish Theme'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
